import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { useIsMobile } from 'pricing-pages-ui-components/hooks/useMedia';
import ErrorPage from 'pricing-pages-ui/pages/ErrorPage';
import { useMemo } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { FREE } from 'self-service-api/constants/ProductLevels';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { getPrimaryTracker } from 'usage-tracker-container';
import MobileRoutes from './MobileRoutes';
import LoadingScreen from './components/LoadingScreen';
import { makeLoadable } from './components/common/LoadableWrapper';
import { ENTERPRISE, tabIdToProductLevels } from './constants/routing/Tabs';
import { useGlobalLoading } from './hooks/loadingHooks';
import { useCommerceHub } from './hooks/useCommerceHub';
import { useCompanyDetails } from './hooks/useCompanyDetails';
import { useRecommendedRouting } from './hooks/useRecommendations';
import { useShouldHideDataOrSuitePage } from './hooks/useShouldHideDataOrSuitePage';
import AppLayout from './layouts/AppLayout';
import AddOnsPage from './pages/AddOnsPageV1';
import BundlePage from './pages/BundlePage';
import CommerceHubPage from './pages/CommerceHubPage';
import FreeToolsPageV1 from './pages/FreeToolsPageV1';
import HubSpotCreditsPage from './pages/HubSpotCreditsPage';
import MarketingPlusPage from './pages/MarketingPlusPage';
import ProductPage from './pages/ProductPage';
import SmartCrmPage from './pages/SmartCrmPage';
import SuitePage from './pages/SuitePage';
import { useAppSelector } from './reducers';
import { isAppAuthSelector } from './selectors/AuthSelectors';
import { isUngatedForHubSpotCreditsPageV2Selector, isUngatedForM1HubPagesSelector, isUngatedForM2BundlePagesSelector, isUngatedForM3SmartCrmPagesSelector, isUngatedForSeatsSelector } from './selectors/GatingSelectors';
import { PageWrapper } from './v2/PageWrapper';
import { PricingPageApp } from './v2/PricingPageApp';
import { useCrmSuiteOwnershipLevel, useIsFreePortal } from './v2/hooks/inventory/useProductInventory';
import { CommerceHubPage as CommerceHubPageV2 } from './v2/pages/commerce/CommerceHubPage';
import { ContentHubPage } from './v2/pages/content/ContentHubPage';
import { CustomerPlatformPage } from './v2/pages/customer-platform/CustomerPlatformPage';
import { DataHubPage } from './v2/pages/data/DataHubPage';
import { FreeToolsPage as FreeToolsPageV2 } from './v2/pages/free/FreeToolsPage';
import { HubspotCreditsPage as HubspotCreditsPageV2 } from './v2/pages/hubspot-credits/HubSpotCredits';
import { MarketingPlusPage as MarketingPlusPageV2 } from './v2/pages/marketing-plus/MarketingPlusPage';
import { MarketingHubPage } from './v2/pages/marketing/MarketingHubPage';
import { SalesHubPage } from './v2/pages/sales/SalesHubPage';
import { ServiceHubPage } from './v2/pages/service/ServiceHubPage';
import { SmartCrmPage as SmartCrmPageV2 } from './v2/pages/smart-crm/SmartCrmPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const createV2PricingPageComponent = productLine => {
  const V2PricingPageComponent = () => {
    let PageComponent = null;
    switch (productLine) {
      case 'MARKETING':
        PageComponent = /*#__PURE__*/_jsx(MarketingHubPage, {});
        break;
      case 'SALES':
        PageComponent = /*#__PURE__*/_jsx(SalesHubPage, {});
        break;
      case 'SERVICE':
        PageComponent = /*#__PURE__*/_jsx(ServiceHubPage, {});
        break;
      case 'CMS':
        PageComponent = /*#__PURE__*/_jsx(ContentHubPage, {});
        break;
      case 'OPERATIONS':
        PageComponent = /*#__PURE__*/_jsx(DataHubPage, {});
        break;
      case 'SUITE':
        return /*#__PURE__*/_jsx(PageWrapper, {
          navItem: "suite",
          children: /*#__PURE__*/_jsx(CustomerPlatformPage, {})
        });
      default:
        return null;
    }
    return /*#__PURE__*/_jsx(PageWrapper, {
      productLine: productLine,
      children: PageComponent
    });
  };
  return V2PricingPageComponent;
};
const createAppAuthComponent = (Component, productLine) => {
  const AppAuthComponent = () => /*#__PURE__*/_jsx(Component, {
    productLine: productLine
  });
  return AppAuthComponent;
};
const createTabComponent = (Component, productLine, path) => {
  const TabComponent = ({
    match: {
      params: {
        tab
      }
    }
  }) => {
    if (!tab || !Object.keys(tabIdToProductLevels).includes(tab)) {
      getPrimaryTracker().setProperties({
        defaultedTab: ENTERPRISE
      });
      return /*#__PURE__*/_jsx(Redirect, {
        to: createToObject(`${path}/${ENTERPRISE}`)
      });
    }
    return /*#__PURE__*/_jsx(Component, {
      productLine: productLine
    });
  };
  return TabComponent;
};
const PathRedirect = ({
  from,
  to
}) => /*#__PURE__*/_jsx(Route, {
  path: from,
  render: ({
    location
  }) => {
    return /*#__PURE__*/_jsx(Redirect, {
      to: Object.assign({}, location, {
        pathname: location.pathname.replace(from, to)
      })
    });
  }
});
const ProductPageRoute = ({
  path,
  Component = ProductPage,
  productLine
}) => {
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const isUngatedForM1HubPages = useAppSelector(isUngatedForM1HubPagesSelector);
  const isUngatedForM2BundlePages = useAppSelector(isUngatedForM2BundlePagesSelector);
  const V2PricingPageComponent = useMemo(() => {
    if (isUngatedForM1HubPages && productLine) {
      return createV2PricingPageComponent(productLine);
    }
    if (path === '/suite' && isUngatedForM2BundlePages) {
      return createV2PricingPageComponent('SUITE');
    }
    return null;
  }, [isUngatedForM1HubPages, isUngatedForM2BundlePages, path, productLine]);
  if (V2PricingPageComponent) {
    return /*#__PURE__*/_jsx(Route, {
      path: path,
      component: V2PricingPageComponent
    });
  }
  if (isAppAuth) {
    const AppAuthComponent = createAppAuthComponent(Component, productLine);
    return /*#__PURE__*/_jsx(Route, {
      path: path,
      component: AppAuthComponent
    });
  }
  const TabComponent = createTabComponent(Component, productLine, path);
  return /*#__PURE__*/_jsx(Route, {
    path: `${path}/:tab?`,
    component: TabComponent
  });
};
const InterstitialPage = makeLoadable({
  loader: () => import( /* webpackChunkName: "interstitial-page" */'pricing-pages-ui/components/interstitial/InterstitialPage')
});
const DefaultInAppRoute = () => {
  const {
    recommendedRoute,
    loading: isLoadingRecommendedRoute
  } = useRecommendedRouting();
  const isAppReady = useGlobalLoading();
  const {
    loading: isCompanyDetailsLoading,
    isVSBPortal
  } = useCompanyDetails();
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const {
    crmSuiteLevel: ownedSuiteProductLevel
  } = useCrmSuiteOwnershipLevel();
  const {
    isFreePortal,
    loading: isInventoryLoading
  } = useIsFreePortal();
  const isFreeVSBPortal = isVSBPortal && isFreePortal;
  const ownsSuiteProduct = isUngatedForSeats && ownedSuiteProductLevel && ownedSuiteProductLevel !== FREE;
  if (!isAppReady || isLoadingRecommendedRoute || isCompanyDetailsLoading || isInventoryLoading) {
    return /*#__PURE__*/_jsx(LoadingScreen, {});
  }
  if (isFreeVSBPortal) {
    return /*#__PURE__*/_jsx(Redirect, {
      from: "/",
      exact: true,
      to: createToObject('/crm')
    });
  }
  if (ownsSuiteProduct) {
    return /*#__PURE__*/_jsx(Redirect, {
      from: "/",
      exact: true,
      to: createToObject('/suite')
    });
  }
  return /*#__PURE__*/_jsx(Redirect, {
    from: "/",
    exact: true,
    to: createToObject(`/${recommendedRoute}`)
  });
};

// *************** Important when adding new routes: ***************
// This component is shared by both the public and authed app.
// The public app enables nested routes within a hub to provide a view of each
// product by tier.
// The authed app disables nested routes within a product, with a check and
// redirect to the following regex path: path="/:page/*"
// When adding a new nested route to the auth app, add a check in the same line
// as the regex redirect to override this behavior

export default function Routes() {
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const shouldHideDataOrSuitePage = useShouldHideDataOrSuitePage();
  const isUngatedForM1HubPages = useAppSelector(isUngatedForM1HubPagesSelector);
  const isUngatedForM2BundlePages = useAppSelector(isUngatedForM2BundlePagesSelector);
  const isUngatedForM3SmartCrmPages = useAppSelector(isUngatedForM3SmartCrmPagesSelector);
  const isUngatedForHubSpotCreditsPageV2 = useAppSelector(isUngatedForHubSpotCreditsPageV2Selector);
  const {
    loading: commerceHubLoading,
    showCommerceHub
  } = useCommerceHub();
  getPrimaryTracker().setProperties({
    isSeatsPilot: isUngatedForSeats
  });
  const isMobile = useIsMobile();
  const history = useHistory();
  if (isMobile && !isAppAuth) {
    return /*#__PURE__*/_jsx(MobileRoutes, {});
  }
  return /*#__PURE__*/_jsx(AppLayout, {
    children: /*#__PURE__*/_jsxs(Switch, {
      children: [isAppAuth && !history.location.pathname.startsWith('/internal-development') && /*#__PURE__*/_jsx(Route, {
        path: "/:page/*",
        render: ({
          location,
          match: {
            params: {
              page
            }
          }
        }) => {
          return /*#__PURE__*/_jsx(Redirect, {
            to: Object.assign({}, location, {
              pathname: `/${page}`
            })
          });
        }
      }), shouldHideDataOrSuitePage && /*#__PURE__*/_jsx(Route, {
        path: ['/data', '/operations', '/suite'],
        children: /*#__PURE__*/_jsx(Redirect, {
          to: "/marketing"
        })
      }), /*#__PURE__*/_jsx(PathRedirect, {
        from: "/splash",
        to: "/suite"
      }), /*#__PURE__*/_jsx(PathRedirect, {
        from: "/cms",
        to: "/content"
      }), /*#__PURE__*/_jsx(PathRedirect, {
        from: "/operations",
        to: "/data"
      }), /*#__PURE__*/_jsx(PathRedirect, {
        from: "/breeze-intelligence",
        to: "/hubspot-credits"
      }), /*#__PURE__*/_jsx(Route, {
        path: "/interstitial",
        render: () => {
          return /*#__PURE__*/_jsx(InterstitialPage, {});
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/bundle",
        component: BundlePage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/marketing-plus",
        render: () => {
          if (isUngatedForM2BundlePages) {
            return /*#__PURE__*/_jsx(PageWrapper, {
              navItem: "marketing-plus",
              children: /*#__PURE__*/_jsx(MarketingPlusPageV2, {})
            });
          }
          return /*#__PURE__*/_jsx(MarketingPlusPage, {});
        }
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/suite",
        Component: SuitePage
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/sales",
        productLine: SALES
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/service",
        productLine: SERVICE
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/content",
        productLine: CMS
      }), /*#__PURE__*/_jsx(Route, {
        path: "/commerce",
        render: () => {
          if (isUngatedForM1HubPages) {
            return /*#__PURE__*/_jsx(PageWrapper, {
              productLine: "COMMERCE",
              children: /*#__PURE__*/_jsx(CommerceHubPageV2, {})
            });
          }
          if (commerceHubLoading) {
            return /*#__PURE__*/_jsx(LoadingScreen, {});
          }
          if (!showCommerceHub) {
            return /*#__PURE__*/_jsx(Redirect, {
              to: createToObject('/suite')
            });
          }
          return /*#__PURE__*/_jsx(CommerceHubPage, {});
        }
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/marketing",
        productLine: MARKETING
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/data",
        productLine: OPERATIONS
      }), /*#__PURE__*/_jsx(Route, {
        path: "/smart-crm",
        render: () => {
          if (isUngatedForM3SmartCrmPages) {
            return /*#__PURE__*/_jsx(PageWrapper, {
              navItem: "smart-crm",
              children: /*#__PURE__*/_jsx(SmartCrmPageV2, {})
            });
          }
          return /*#__PURE__*/_jsx(SmartCrmPage, {});
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/crm",
        render: () => {
          if (isUngatedForM2BundlePages) {
            return /*#__PURE__*/_jsx(PageWrapper, {
              navItem: "crm",
              children: /*#__PURE__*/_jsx(FreeToolsPageV2, {})
            });
          }
          return /*#__PURE__*/_jsx(FreeToolsPageV1, {});
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/add-ons",
        component: AddOnsPage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/hubspot-credits",
        render: () => {
          if (isUngatedForHubSpotCreditsPageV2) {
            return /*#__PURE__*/_jsx(PageWrapper, {
              navItem: "hubspot-credits",
              children: /*#__PURE__*/_jsx(HubspotCreditsPageV2, {})
            });
          }
          return /*#__PURE__*/_jsx(HubSpotCreditsPage, {});
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/internal-development",
        component: PricingPageApp
      }), isAppAuth ? /*#__PURE__*/_jsx(DefaultInAppRoute, {}) : /*#__PURE__*/_jsx(Redirect, {
        from: "/",
        exact: true,
        to: createToObject('/suite')
      }), /*#__PURE__*/_jsx(Route, {
        component: ErrorPage
      })]
    })
  });
}